import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { BankingEntitiesBankAccountNumberFormatEnum } from 'generated/resources/datatable.graphql'
import { EmployeeBlocksEmployeeFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useBankInfoForm } from './form'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  canSeeBankInfo?: boolean
  canEditPersonalInfo: boolean
}

export const BankInfo = ({ employee, canEditPersonalInfo, canSeeBankInfo }: Props) => {
  const readonly = !canEditPersonalInfo
  const { form, showSubmit, submitting, bankAccountFormatOptions, accountLabel, extraFieldLabel } =
    useBankInfoForm(employee, readonly)
  const { addBankNumber, addSwiftBic } = trackingEvents.employee.personal

  if (!canSeeBankInfo) return null

  return (
    <FormComponent onSubmit={() => form.handleSubmit()}>
      {!readonly && (
        <FormRow>
          <Select
            label={i18n.t('employee_form.bank_info.format')}
            value={form.values.format}
            options={bankAccountFormatOptions()}
            onChange={(value) => {
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              const newFormat = value as BankingEntitiesBankAccountNumberFormatEnum

              if (newFormat == 'other' || newFormat == 'clabe') {
                form.setValues({
                  ...form.values,
                  complementaryData: '',
                  format: newFormat,
                })
              } else {
                form.setValues({
                  ...form.values,
                  format: newFormat,
                })
              }
            }}
          />
        </FormRow>
      )}
      <FormRow>
        <TextField
          mandatory
          label={accountLabel}
          onChange={(value) => form.setValues({ ...form.values, accountNumber: value })}
          value={form.values.accountNumber}
          readonly={readonly}
          onBlur={() => track(addBankNumber)}
          error={form.errors.accountNumber}
        />
      </FormRow>
      {extraFieldLabel && (
        <FormRow>
          <TextField
            label={extraFieldLabel}
            onChange={(value) => form.setValues({ ...form.values, complementaryData: value })}
            value={form.values.complementaryData}
            readonly={readonly}
            onBlur={() => track(addSwiftBic)}
            error={form.errors.complementaryData}
          />
        </FormRow>
      )}
      {showSubmit && (
        <FormRow footer>
          <Button disabled={submitting} submit>
            {i18n.t('common.submit')}
          </Button>
        </FormRow>
      )}
    </FormComponent>
  )
}

const BankInfoWrapper = (props: Props) => {
  if (!props.canSeeBankInfo) return null

  return (
    <PanesPadForm direction="horizontal" boxed>
      <PanesHeader
        illustration="cash"
        title={i18n.t('employee_form.bank_info.title')}
        description={i18n.t('employee_form.bank_info.description')}
      />
      <BankInfo {...props} />
    </PanesPadForm>
  )
}

export default observer(BankInfoWrapper)
